import React, { useState, useCallback, useEffect } from "react";

import {
    Box,
    Paper,
    TextField,
    Grid,
    Switch,
    FormControlLabel,
    styled,
    Card,
    InputLabel,
    FormControl,
    Select,
    MenuItem,
    FormHelperText
} from '@mui/material';

import {
    LoadingButton
} from '@mui/lab';

import { useNavigate, useParams } from "react-router-dom";
import { useFormik } from 'formik';
import CommonBreadcumbs from '../../common/CommonBreadcumbs';
import CommonSectionHeader from "../../common/CommonSectionHeader";
import CommonRestricted from '../../common/CommonRestricted';

import { useAppContextFeatures } from '../../../context/AppContext';

import { useSystemPrompt } from "../../../hooks/useSystemPrompt";
import { useSchedulers, schedulerTypes, targetTypes, schedulerConfigs } from '../../../hooks/api/useSchedulers';
import { initialValues, validationSchema } from "../../../schema/scheduler/taskSchema";
import { useSystemRoutes, routeTypes } from '../../../hooks/useSystemRoutes';
import { useSystemHead, headTitles } from '../../../hooks/useSystemHead';
import { usePermissions, scopeIds, scopeTypes, functionTypes } from '../../../hooks/usePermissions';

const StyledCard = styled(Card)(({ theme }) => ({
    padding: theme.spacing(2)
}));

export default function UpdateTask({
    routeType
}) {

    const { id } = useParams();

    const { promptMessage } = useSystemPrompt();

    const { isLoading, update, get } = useSchedulers({ schedulerType: schedulerTypes.schedulerTask, targetType: targetTypes.schedulerTask });
    const { isPermissionAllow } = usePermissions(routeType, scopeIds.scheduler.id, scopeTypes.scheduler.task.id)
    const { setSystemLoading } = useAppContextFeatures();

    const navigate = useNavigate();
    const [savedValues, setSavedValues] = useState(null)
    const { getRoute } = useSystemRoutes(routeType);
    const { getHtmlHead } = useSystemHead(routeType)

    const loadData = useCallback(async (id) => {

        setSystemLoading(true);

        try {
            const { data } = await get({ id })
            const { scheduler_task: schedulerTask } = data;

            // locate back the integration Id
            let taskId = schedulerTask?.task_key
            let integrationId = '';

            schedulerConfigs.schedulerIntegrations.forEach(i => {
                if (i.tasks.length) {
                    i.tasks.forEach(e2 => {
                        if (e2.id === taskId) {
                            integrationId = i.id
                        }
                    })
                }
            })

            setSavedValues({
                integrationId,
                taskId: schedulerTask?.task_key,
                taskName: schedulerTask?.name,
                description: schedulerTask?.description,
                status: schedulerTask?.status === '1'
            })

        } catch (error) {
            promptMessage(false, `${error?.message}`)
        }

        setSystemLoading(false)

    }, [id])

    useEffect(() => {

        loadData(id);

    }, [])

    const onSubmit = async (currentValues) => {

        console.log('currentValues', currentValues)

        try {

            let data = {
                scheduler_task: {
                    integration_id: currentValues?.integrationId,
                    task_key: currentValues?.taskId,
                    name: currentValues?.taskName,
                    description: currentValues?.description,
                    status: currentValues?.status ? 1 : 0,
                }
            }

            const response = await update({ id, data });

            promptMessage(true, `${response?.message}`)

            navigate(getRoute("settings/schedulers"))

        } catch (error) {
            promptMessage(false, `Action Failed (${error?.message})`)
        }
    }

    const formik = useFormik({
        initialValues: savedValues,
        validationSchema,
        enableReinitialize: true,
        onSubmit
    });

    const { values, errors, touched, handleChange, setFieldValue } = formik;


    const getAvailableTasks = () => {

        return schedulerConfigs.getSchedulerTasks(
            schedulerConfigs.schedulerIntegrations,
            values?.integrationId ? values.integrationId : ""
        )
    }

    const RenderForm = () => {

        if (!values) return <></>

        return (

            <form onSubmit={formik.handleSubmit}>
                <StyledCard>
                    <Grid container spacing={1}>
                        <Grid item xs={12} sm={12} md={4} textAlign={"left"}>

                            <CommonSectionHeader
                                title={'Information'}
                                subTitle={"Provide details of the profile"}
                            />

                        </Grid>

                        <Grid item xs={12} sm={12} md={8}>
                            <Grid container spacing={1}>

                                <Grid item xs={12} sm={12} md={6} textAlign="left">
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                name="status"
                                                checked={Boolean(values.status)}
                                                onChange={(event, checked) => {
                                                    setFieldValue("status", checked);
                                                }}
                                            />
                                        }
                                        label={
                                            Boolean(values.status) ? "Enabled" : "Disabled"
                                        }
                                    />
                                </Grid>

                                <Grid item xs={6}>
                                    {/* Space down */}
                                </Grid>

                                <Grid item xs={12} sm={12} md={6}>

                                    <FormControl
                                        margin="dense"
                                        fullWidth
                                        size="small"
                                        error={Boolean(errors?.integrationId)}
                                    >
                                        <InputLabel id="integrationId">Integration</InputLabel>
                                        <Select
                                            fullWidth
                                            labelId="integrationId"
                                            label="Integration"
                                            name={"integrationId"}
                                            onChange={(e) => {
                                                setFieldValue(`taskId`, '')
                                                setFieldValue(`integrationId`, e.target.value)
                                            }}
                                            value={values?.integrationId ? values.integrationId : ""}
                                        >
                                            {
                                                schedulerConfigs.schedulerIntegrationTypes.map((i, index) => {
                                                    return (
                                                        <MenuItem
                                                            key={index}
                                                            value={i.id}
                                                        >
                                                            {i.name} ({i.id})
                                                        </MenuItem>
                                                    )
                                                })
                                            }
                                        </Select>
                                        <FormHelperText>
                                            {errors?.integrationId}
                                        </FormHelperText>
                                    </FormControl>

                                </Grid>

                                <Grid item xs={12} sm={12} md={6}>

                                    <FormControl
                                        margin="dense"
                                        fullWidth
                                        size="small"
                                        error={Boolean(errors?.taskId)}
                                    >
                                        <InputLabel id="taskId">Available Task</InputLabel>
                                        <Select
                                            fullWidth
                                            labelId="taskId"
                                            label="Available Tasks"
                                            name={"taskId"}
                                            onChange={handleChange}
                                            value={values?.taskId ? values.taskId : ""}
                                        >
                                            {
                                                getAvailableTasks().map((i, index) => {
                                                    return (
                                                        <MenuItem
                                                            key={index}
                                                            value={i.id}
                                                        >
                                                            {i.name} ({i.id})
                                                        </MenuItem>
                                                    )
                                                })
                                            }
                                        </Select>
                                        <FormHelperText>
                                            {errors?.taskId}
                                        </FormHelperText>
                                    </FormControl>

                                </Grid>

                                <Grid item xs={12} sm={12} md={6}>
                                    <TextField
                                        margin="dense"
                                        size="small"
                                        autoFocus
                                        fullWidth
                                        name={"taskName"}
                                        label="Name"
                                        type="text"
                                        variant="outlined"
                                        value={values.taskName}
                                        onChange={handleChange}
                                        error={touched.taskName && Boolean(errors.taskName)}
                                        helperText={touched.taskName && errors.taskName}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={12} md={12}>
                                    <TextField
                                        margin="dense"
                                        size="small"
                                        autoFocus
                                        fullWidth
                                        name={"description"}
                                        label="Description"
                                        type="text"
                                        variant="outlined"
                                        multiline
                                        rows={3}
                                        value={values.description}
                                        onChange={handleChange}
                                        error={touched.description && Boolean(errors.description)}
                                        helperText={touched.description && errors.description}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>

                    </Grid>
                </StyledCard>
                <Grid container spacing={1} sx={{ pl: 2, pr: 2 }}>
                    <Grid item md={12} textAlign={"right"}>
                        <LoadingButton
                            type="submit"
                            // fullWidth
                            variant="contained"
                            sx={{
                                mt: 3,
                                mb: 2
                            }}
                            loading={isLoading}
                        >
                            Save Changes
                        </LoadingButton>
                    </Grid>
                </Grid>
            </form>

        )
    }

    return (
        <Box>
            {
                getHtmlHead({ routeType, title: headTitles.updateTask })
            }

            <CommonBreadcumbs
                routeType={routeType}
                pageTitle="Update Scheduler Task"
                breadcrumbs={[
                    {
                        displayText: "Scheduler Tasks",
                        props: {
                            href: getRoute("settings/schedulers")
                        }
                    },
                    {
                        displayText: "Update Scheduler Task",
                        props: {
                            color: 'secondary.main'
                        }
                    },
                ]}
            />

            {
                isPermissionAllow({ functionType: functionTypes.pageUpdate.id }) ?
                    RenderForm()
                    :
                    <CommonRestricted />
            }

        </Box>
    )
}